import * as i0 from '@angular/core';
import { Directive, HostListener, EventEmitter, Component, ViewEncapsulation, Host, ContentChildren, Input, HostBinding, Output, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { delay, map, switchMap, takeUntil, startWith } from 'rxjs/operators';
import { ReplaySubject, Subject, BehaviorSubject, zip, merge } from 'rxjs';
import * as i1 from '@smartdesign/angular/common';

const dropdownTimings = '150ms ease';
const hiddenState = { opacity: 0, visibility: 'hidden' };
const visibleState = { opacity: 1, visibility: 'visible' };
const defaultHidden = Object.assign(Object.assign({}, hiddenState), { transform: 'scaleY(0.9) translateY(-40px)' });
const defaultVisible = Object.assign(Object.assign({}, visibleState), { transform: 'scaleY(1) translateY(0)' });
const bottomRightHidden = Object.assign(Object.assign({}, hiddenState), { transform: 'scaleY(0.9) translateY(40px)' });
const bottomRightVisible = Object.assign(Object.assign({}, visibleState), { transform: 'scaleY(1) translateY(100%)' });
const topHidden = Object.assign(Object.assign({}, hiddenState), { transform: 'scaleY(0.9) translateY(-40px)' });
const topVisible = Object.assign(Object.assign({}, visibleState), { transform: 'scaleY(1) translateY(-100%)' });
const dropdown = trigger('toggle', [
    transition('void => default', [style(defaultHidden), animate(dropdownTimings, style(defaultVisible))]),
    transition('default => void', [style(defaultVisible), animate(dropdownTimings, style(defaultHidden))]),
    transition('void => bottomRight', [style(bottomRightHidden), animate(dropdownTimings, style(bottomRightVisible))]),
    transition('bottomRight => void', [style(bottomRightVisible), animate(dropdownTimings, style(bottomRightHidden))]),
    transition('void => topRight', [style(topHidden), animate(dropdownTimings, style(topVisible))]),
    transition('topRight => void', [style(topVisible), animate(dropdownTimings, style(topHidden))]),
    transition('void => topLeft', [style(topHidden), animate(dropdownTimings, style(topVisible))]),
    transition('topLeft => void', [style(topVisible), animate(dropdownTimings, style(topHidden))]),
]);

class MenuTriggerDirective {
    constructor() {
        this.menuClick = new ReplaySubject();
    }
    onClick() {
        this.menuClick.next();
    }
}
MenuTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: MenuTriggerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MenuTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: MenuTriggerDirective, selector: "[sdMenuTrigger]", host: { listeners: { "click": "onClick()" } }, exportAs: ["sdMenuTrigger"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: MenuTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdMenuTrigger]',
                    exportAs: 'sdMenuTrigger',
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class MenuComponent {
    constructor(hostRef, outsideClickListener) {
        this.hostRef = hostRef;
        this.outsideClickListener = outsideClickListener;
        this.destroyed$ = new Subject();
        this.observedElements$ = new BehaviorSubject([]);
        this.closeOnOutside = false;
        this.show = false;
        this.position = 'default';
        this.menu = true;
        this.insideClick = new EventEmitter();
        this.outsideClick = new EventEmitter();
        this.observedElements = this.hostRef;
    }
    set observedElements(value) {
        let result;
        if (!value) {
            result = [];
        }
        else if (Array.isArray(value)) {
            result = value;
        }
        else {
            result = [value];
        }
        this.observedElements$.next([this.hostRef, ...result]);
    }
    get classes() {
        return {
            'menu--bottom-right': this.position === 'bottomRight',
            'menu--top-right': this.position === 'topRight',
            'menu--top-left': this.position === 'topLeft',
        };
    }
    ngOnInit() {
        this.observedElements$
            .pipe(delay(500), // do not track initial open click
        map((elements) => elements
            .map((element) => ('hostRef' in element ? element.hostRef : element))
            .map((element) => this.outsideClickListener.create(element))), switchMap((events) => zip(...events)), map((types) => types.some((type) => type === 'INSIDE')), takeUntil(this.destroyed$))
            .subscribe((inside) => {
            if (inside) {
                this.insideClick.next();
            }
            else {
                this.outsideClick.next();
                if (this.closeOnOutside) {
                    this.show = false;
                }
            }
        });
    }
    ngAfterViewInit() {
        this.menuTriggerRefs.changes
            .pipe(startWith(this.menuTriggerRefs), takeUntil(this.destroyed$), map((refs) => Array.from(refs).map((ref) => ref.menuClick)), switchMap((refs$) => merge(...refs$)))
            .subscribe(() => (this.show = !this.show));
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
}
MenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: MenuComponent, deps: [{ token: i0.ElementRef, host: true }, { token: i1.OutsideClickListenerService }], target: i0.ɵɵFactoryTarget.Component });
MenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: MenuComponent, selector: "sd-menu", inputs: { closeOnOutside: "closeOnOutside", show: "show", position: "position", observedElements: "observedElements" }, outputs: { insideClick: "insideClick", outsideClick: "outsideClick" }, host: { properties: { "class": "this.classes", "class.menu": "this.menu" } }, queries: [{ propertyName: "menuTriggerRefs", predicate: MenuTriggerDirective }], exportAs: ["sdMenu"], ngImport: i0, template: "<ng-content select=\"[sdMenuTrigger]\"></ng-content>\n<div class=\"menu__item\" [@toggle]=\"position\" *ngIf=\"show\">\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";.menu{position:relative;display:inline-block}.menu__item{position:absolute;z-index:20;width:100%;max-height:320px;padding:.5rem 0;overflow-y:auto;white-space:normal;cursor:pointer;background-color:var(--color-white);border-radius:.5rem;box-shadow:0 -2px 6px rgba(0,0,0,.04),0 2.5px 2.5px rgba(0,0,0,.06),0 8px 16px rgba(50,50,93,.07);margin-top:.5rem;margin-bottom:.5rem;padding:.5rem}.menu__item::-webkit-scrollbar-thumb{background-color:var(--color-grey-200);background-clip:padding-box;border:4px solid transparent;border-radius:8px}.menu__item::-webkit-scrollbar{width:16px}.menu__item ul li{padding:.75rem 1rem;font-size:1rem;line-height:24px;letter-spacing:-.01em;color:var(--color-grey-700);border-radius:.25rem}.menu__item ul li:hover,.menu__item ul li:focus{background-color:var(--color-grey-200);outline:none}.menu__item ul li:active,.menu__item ul li.is-selected,.menu__item ul li:focus{color:var(--color-white);background-color:var(--color-secondary-400)}.menu__item ul .has-nesting{padding:0 0 0 1rem}.menu__item ul .has-nesting:hover,.menu__item ul .has-nesting:focus{background-color:var(--color-white)}.menu__item ul .has-nesting:active,.menu__item ul .has-nesting.is-selected{color:var(--color-grey-700);background-color:var(--color-white)}.menu--full-width{width:100%}.menu.menu--bottom-right .menu__item{right:0;bottom:-1rem;transform:translateY(100%)}.menu.menu--top-right .menu__item{top:-1rem;right:0;transform:translateY(-100%)}.menu.menu--top-left .menu__item{top:-1rem;left:0;transform:translateY(-100%)}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [dropdown], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sd-menu', animations: [dropdown], encapsulation: ViewEncapsulation.None, exportAs: 'sdMenu', template: "<ng-content select=\"[sdMenuTrigger]\"></ng-content>\n<div class=\"menu__item\" [@toggle]=\"position\" *ngIf=\"show\">\n  <ng-content></ng-content>\n</div>\n", styles: ["@charset \"UTF-8\";.menu{position:relative;display:inline-block}.menu__item{position:absolute;z-index:20;width:100%;max-height:320px;padding:.5rem 0;overflow-y:auto;white-space:normal;cursor:pointer;background-color:var(--color-white);border-radius:.5rem;box-shadow:0 -2px 6px rgba(0,0,0,.04),0 2.5px 2.5px rgba(0,0,0,.06),0 8px 16px rgba(50,50,93,.07);margin-top:.5rem;margin-bottom:.5rem;padding:.5rem}.menu__item::-webkit-scrollbar-thumb{background-color:var(--color-grey-200);background-clip:padding-box;border:4px solid transparent;border-radius:8px}.menu__item::-webkit-scrollbar{width:16px}.menu__item ul li{padding:.75rem 1rem;font-size:1rem;line-height:24px;letter-spacing:-.01em;color:var(--color-grey-700);border-radius:.25rem}.menu__item ul li:hover,.menu__item ul li:focus{background-color:var(--color-grey-200);outline:none}.menu__item ul li:active,.menu__item ul li.is-selected,.menu__item ul li:focus{color:var(--color-white);background-color:var(--color-secondary-400)}.menu__item ul .has-nesting{padding:0 0 0 1rem}.menu__item ul .has-nesting:hover,.menu__item ul .has-nesting:focus{background-color:var(--color-white)}.menu__item ul .has-nesting:active,.menu__item ul .has-nesting.is-selected{color:var(--color-grey-700);background-color:var(--color-white)}.menu--full-width{width:100%}.menu.menu--bottom-right .menu__item{right:0;bottom:-1rem;transform:translateY(100%)}.menu.menu--top-right .menu__item{top:-1rem;right:0;transform:translateY(-100%)}.menu.menu--top-left .menu__item{top:-1rem;left:0;transform:translateY(-100%)}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: i1.OutsideClickListenerService }];
    }, propDecorators: { menuTriggerRefs: [{
                type: ContentChildren,
                args: [MenuTriggerDirective]
            }], closeOnOutside: [{
                type: Input
            }], show: [{
                type: Input
            }], position: [{
                type: Input
            }], observedElements: [{
                type: Input
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }], menu: [{
                type: HostBinding,
                args: ['class.menu']
            }], insideClick: [{
                type: Output
            }], outsideClick: [{
                type: Output
            }] } });

class SmartDesignMenuModule {
}
SmartDesignMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartDesignMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignMenuModule, declarations: [MenuComponent, MenuTriggerDirective], imports: [CommonModule], exports: [MenuComponent, MenuTriggerDirective] });
SmartDesignMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignMenuModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MenuComponent, MenuTriggerDirective],
                    imports: [CommonModule],
                    exports: [MenuComponent, MenuTriggerDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MenuComponent, MenuTriggerDirective, SmartDesignMenuModule, dropdown };
